import { useState } from 'react';
import { Button } from '@antimatterhq/ui/components/button';
import { useColorMode } from '@antimatterhq/ui/contexts/ColorModeContext';
import { Text } from '@antimatterhq/ui/components/typography';
import { cn } from '@antimatterhq/ui/lib/utils';
import { Checkbox } from '@antimatterhq/ui/components/checkbox';
import { Label } from '@antimatterhq/ui/components/label';
import { ScrollArea } from '@antimatterhq/ui/components/scrollArea';
import { TermsOfService } from './TermsOfService';
import { useAcceptTermOptions } from '@/hooks/workspace/useAcceptTermOptions';
import { useOptimisticAcceptTerms } from '@/hooks/workspace/useGetTermsOptions';

const AcceptTermsDialog: React.FunctionComponent<{
  daslWorkspaceId: string;
}> = ({ daslWorkspaceId }) => {
  const { isDarkMode } = useColorMode();
  const { mutateAsync: acceptTerms } = useAcceptTermOptions(daslWorkspaceId);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const optimisticAcceptTerms = useOptimisticAcceptTerms(daslWorkspaceId);

  const handleAcceptTerms = async () => {
    await acceptTerms(
      {
        data: {
          accept: true,
        },
      },
      {
        toasts: {
          success: 'Terms accepted!',
          error: 'Failed to accept terms. Please try again.',
        },
        onSuccess: async () => {
          optimisticAcceptTerms(true);
        },
      },
    );
  };

  return (
    <>
      <div className={cn('fixed inset-0 z-50 bg-grey-500/80')} />
      <div className="fixed left-[50%] top-[50%] z-50 grid max-h-[100vh] w-full max-w-4xl translate-x-[-50%] translate-y-[-50%] gap-4 border border-grey-200 bg-white p-6 shadow-lg duration-200 dark:border-grey-650 dark:bg-grey-800 sm:rounded-lg">
        <div className="absolute -top-14 left-1/2 -translate-x-1/2">
          <img
            src={
              isDarkMode
                ? '/images/logo/antimatter-full-logo-dark.svg'
                : '/images/logo/antimatter-full-logo-light.svg'
            }
            alt="avatar"
            className="h-8 w-auto object-contain"
          />
        </div>
        <Text size="lg" className="font-bold">
          Please accept the terms of the DASL Private Preview
        </Text>
        <ScrollArea className="!dark:bg-white/5 max-full max-h-96 rounded-md bg-black/5">
          <Text>
            <TermsOfService />
          </Text>
        </ScrollArea>
        <div className="flex items-center gap-2">
          <Checkbox
            aria-labelledby="accept-terms-label"
            id="accept-terms"
            checked={acceptedTerms}
            onCheckedChange={(checked) =>
              setAcceptedTerms(checked === 'indeterminate' ? false : checked)
            }
          />
          <Label htmlFor="accept-terms" mb={0} id="accept-terms-label">
            By checking this box, I confirm that I agree to the Terms of Service
          </Label>
        </div>
        <div className="flex justify-end">
          <Button
            type="button"
            variant="primary"
            disabled={!acceptedTerms}
            onClick={handleAcceptTerms}
          >
            Accept Terms
          </Button>
        </div>
      </div>
    </>
  );
};

export default AcceptTermsDialog;
